import { Component, Inject, Input, OnInit, ViewChild } from "@angular/core";
import { ColDef } from "ag-grid-community";
import { cloneDeep } from "lodash";
import { ClientSideListBaseComponent } from "src/app/common/core/client-side-list-base/client-side-list-base.component";
import { colDefs } from "./billing-daily-notes.config";
import { CommonModule } from "@angular/common";
import { BillingDetailsComponent } from "./billing-details.component";
import { MatIconModule } from "@angular/material/icon";
import { FormsModule } from "@angular/forms";
import {
  ButtonComponent,
  ButtonVariant,
} from "../../../common/components/core/button/button.component";
import {
  CoSignBillingDetails,
  CoSignRequest,
} from "src/app/common/interfaces/cosign-billing.interface";
import { StorageService } from "src/app/common/services/storage.service";
import { CoSignerClient } from "src/app/common/services/co-signer.client";
import { debounceTime, Subject } from "rxjs";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationModalComponent } from "src/app/common/components/core/modal/confirmation-modal/confirmation-modal.component";
import { CharacterRestrictionError } from "src/app/common/constants/documentation-constants";
import { SecurityFeatureUtility } from "src/app/common/services/security-feature.utility";
import { AppConstants } from "src/app/common/constants/app-constants";
import { Features } from "src/app/common/enums/features.enum";

@Component({
  selector: "app-billing-daily-notes",
  templateUrl: "./billing-daily-notes.component.html",
  styleUrls: ["./billing-daily-notes.component.scss"],
  standalone: true,
  imports: [
    ClientSideListBaseComponent,
    CommonModule,
    MatIconModule,
    FormsModule,
    ButtonComponent,
    MatTooltipModule,
  ],
})
export class BillingDailyNotesComponent implements OnInit {
  filteredRowData: CoSignBillingDetails[] = [];
  @ViewChild(ClientSideListBaseComponent)
  theraclient!: ClientSideListBaseComponent;
  private searchSubject: Subject<string> = new Subject<string>();
  searchText: string = "";
  columnDefs:  any[] = [];
  selectedRowsToSign: any[] = [];
  detailCellRenderer = BillingDetailsComponent;
  buttonVariant = ButtonVariant;
  rowData: CoSignBillingDetails[] = [];
  currentUser: any;
  loggedInUser: any;
  currentFacility: any;
  enableSign: boolean = false;
  canAccess: boolean = false;

  detailCellRendererParams = {
    getDetailRowData: (params: any) => {
      if (!params.data) {
        params.successCallback([]);
        return;
      }
      if (typeof params.successCallback !== "function") {
        return;
      }
      params.successCallback(params.data);
    },
  };

  constructor(
    @Inject("userId") public userId: string,
    private cosignClient: CoSignerClient,
    private storageService: StorageService,
    private dialog: MatDialog,
    private securityFeatureUtility: SecurityFeatureUtility
  ) {
    this.searchSubject.pipe(debounceTime(800)).subscribe((value: any) => {
      this.onSearch(value);
    });
  }

  ngOnInit(): void {
    this.currentUser = this.userId;
    this.loggedInUser = this.storageService.getUserId();
    this.currentFacility = this.storageService.getSelectedFacilityId();
    this.getCoSignBillingDetails(this.currentFacility, this.currentUser);
    this.canAccess =
      this.securityFeatureUtility.getSecurityFeature(Features.CO_SIGN, false)
        ?.featureaccess === AppConstants.manage;
    this.columnDefs = cloneDeep(colDefs(this));
  }
  typed(event: KeyboardEvent) {
    const value = this.searchText.trim();
    if (value.length > 2) {
      this.searchSubject.next(value);
    }
    else{
      this.searchSubject.next("");
    }
  }

  getCoSignBillingDetails(facilityId: string, cosignerid: string) {
    this.cosignClient
      .getCoSignBillingDetails(facilityId, cosignerid)
      .subscribe((data: any) => {
        this.rowData = data.responseData;
      });
  }

  selectedForCoSign($event: any) {
    if ($event.api.getSelectedRows().length > 0) {
      this.selectedRowsToSign = $event.api
        .getSelectedRows()
        .map((x: any) => x.id);
      this.enableSign = $event.api
        .getSelectedRows()
        .every((x: any) => x.issignenabled);
    } else {
      this.selectedRowsToSign = [];
      this.enableSign = false;
    }
  }

  isRowMaster(data: any): boolean {
    return true;
  }

  onClickSign() {
    if (this.selectedRowsToSign.length > 0 && this.enableSign) {
      let cosignBillingRequest: CoSignRequest = {
        cosignedby: this.loggedInUser,
        ids: this.selectedRowsToSign,
      };
      this.cosignClient
        .signBillingDetails(cosignBillingRequest)
        .subscribe((response: any) => {
          this.getCoSignBillingDetails(this.currentFacility, this.currentUser);
        });
        this.enableSign=false;
    }
  }

  onSearch(keyword: any) {
    if (keyword) {
      this.updateFilter(keyword);
    } else {
      this.filteredRowData = this.rowData;
      this.updateGridData();
    }
  }

  updateFilter(keyword: any) {
    const value = keyword.toString().toLowerCase().trim();
    if (!value) {
      this.filteredRowData = [...this.rowData];
      this.updateGridData();
      return;
    }
    const keys = this.columnDefs
      .filter((x) => x.sortable)
      .map((x) => x["field"]);
    this.filteredRowData = this.rowData.filter((item: any) => {
      return keys.some((key: any) => {
        return item[key] && item[key].toString().toLowerCase().includes(value);
      });
    });
    this.updateGridData();
  }

  private updateGridData() {
    this.theraclient.gridRowData = this.filteredRowData;
    this.theraclient.updateGridData();
  }
}
